import { Panel, CountKpi } from '../components/CommandCentreParts.js';
import { EmptyState } from '../components/NoData.js';
import { useApi, type GetToken } from '../lib/useApi.js';
import { count, NO_DATA } from '../lib/format.js';

interface Step { key: string; label: string; n: number; note?: string }
interface ConversionsResponse {
  window: { days: number; trackingSince: string | null; note: string };
  submissions: { total: number; sinceTracking: number; beforeTracking: number };
  platform: Step[];
  onPage: Step[];
  crm: Step[];
  alternatives: Step[];
  biggestDrop: { from: string; to: string; lost: number; pct: number } | null;
  costGbp: number | null;
  caveats: Record<string, string>;
  generatedUtc: string;
}

/* A bar is only meaningful against a base it is genuinely comparable with, so
   each stage is scaled within itself and never across the consent boundary. */
function Bar({ step, base }: { step: Step; base: number }): JSX.Element {
  const pct = base > 0 ? Math.round((step.n / base) * 1000) / 10 : 0;
  const dead = step.n === 0;
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
        <span style={{ fontWeight: 600 }}>{step.label}</span>
        <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700,
                       color: dead ? 'var(--negative)' : 'var(--text)' }}>
          {count(step.n)}{base > 0 && !dead ? <span style={{ fontWeight: 400, color: 'var(--text-faint)', fontSize: 12 }}> · {pct}%</span> : null}
        </span>
      </div>
      <div style={{ height: 10, background: 'var(--line)', borderRadius: 5, overflow: 'hidden', marginTop: 5 }}>
        <div style={{ width: `${Math.max(pct, dead ? 0 : 1.5)}%`, height: '100%',
                      background: dead ? 'transparent' : 'var(--accent)' }} />
      </div>
      {step.note ? <div style={{ fontSize: 11.5, color: 'var(--text-faint)', marginTop: 4 }}>{step.note}</div> : null}
    </div>
  );
}

export function Conversions({ getToken }: { getToken: GetToken }): JSX.Element {
  const { data, error } = useApi<ConversionsResponse>('/api/conversions?days=30', getToken);

  if (error) return <EmptyState title="Could not load conversions" tag="Error">{error}</EmptyState>;
  if (!data) return <EmptyState title="Loading…" tag="Please wait">Reading the funnel.</EmptyState>;

  const clicks = data.platform.find((s) => s.key === 'clicks')?.n ?? 0;
  const pageViews = data.onPage[0]?.n ?? 0;
  const impressions = data.platform[0]?.n ?? 0;
  const started = data.onPage.find((s) => s.key === 'form_start')?.n ?? 0;
  const anyAlternative = data.alternatives.reduce((s, a) => s + a.n, 0);

  return (
    <div className="stack">
      {pageViews > 0 && started === 0 ? (
        <div className="panel" style={{ borderColor: 'var(--warning)' }}>
          <h2>Nobody is starting the form</h2>
          <p>
            Since on-page tracking was installed
            {data.window.trackingSince
              ? <> on <strong>{new Date(data.window.trackingSince).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}</strong></>
              : null}
            , {count(pageViews)} people reached a landing page and saw the form, and{' '}
            <strong>none has focused a single field</strong>. The loss is at one point rather than
            spread across the funnel, so this is not a page that fails to load or hides the form —
            it is being read and declined.
          </p>
          {data.submissions.beforeTracking > 0 ? (
            <p style={{ marginBottom: 0 }}>
              <strong>This is not the whole picture.</strong>{' '}
              {count(data.submissions.beforeTracking)} genuine enquir
              {data.submissions.beforeTracking === 1 ? 'y was' : 'ies were'} submitted{' '}
              <strong>before tracking existed</strong> and cannot appear in the steps below. The
              funnel measures the window it can see, not the whole campaign.
            </p>
          ) : null}
        </div>
      ) : null}

      <Panel
        title="Before the click"
        subtitle="Measured by Google, for every visitor. Last 30 days."
      >
        <div className="kpi-grid">
          <CountKpi label="Impressions" value={impressions || null} />
          <CountKpi label="Clicks" value={clicks || null} />
          <CountKpi label="Click-through rate" value={impressions > 0 ? Math.round((clicks / impressions) * 1000) / 10 : null} note="% of impressions" dp={1} />
          <CountKpi label="Spend" value={data.costGbp} money />
          <CountKpi label="Cost per click" value={clicks > 0 && data.costGbp !== null ? Math.round((data.costGbp / clicks) * 100) / 100 : null} money dp={2} />
        </div>
      </Panel>

      <div className="panel" style={{ background: 'var(--tint)' }}>
        <strong>Measurement boundary.</strong>{' '}
        <span style={{ color: 'var(--text-muted)' }}>{data.caveats['consentBoundary']}</span>
      </div>

      <Panel
        title="On the page"
        subtitle={`Measured by our own script, for visitors who granted analytics consent. Percentages are of the consented arrivals, never of clicks. ${data.window.note}`}
      >
        {data.onPage.map((s) => <Bar key={s.key} step={s} base={pageViews} />)}
        {data.biggestDrop && data.biggestDrop.lost > 0 ? (
          <div className="ge-note" style={{ marginTop: 6, padding: '12px 16px', background: 'var(--tint)',
                                            borderLeft: '4px solid var(--negative)', borderRadius: '0 8px 8px 0' }}>
            <strong>Biggest single drop:</strong> {data.biggestDrop.from} → {data.biggestDrop.to},
            losing {count(data.biggestDrop.lost)} of them ({data.biggestDrop.pct}%).
          </div>
        ) : null}
      </Panel>

      <Panel
        title="Other ways in"
        subtitle="A funnel that only counts the form scores a phone call as a failure. These are counted alongside it, not inside it."
      >
        {anyAlternative === 0 ? (
          <p style={{ color: 'var(--text-muted)', margin: 0 }}>
            No call-to-action has been clicked yet, on any route.
          </p>
        ) : (
          <table className="data">
            <thead><tr><th>Route</th><th className="num">Clicks</th></tr></thead>
            <tbody>
              {data.alternatives.filter((a) => a.n > 0).map((a) => (
                <tr key={a.key}><td>{a.label}</td><td className="num">{count(a.n)}</td></tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      <Panel
        title="After the form"
        subtitle="Growth Engine cohort only. Pre-launch history is excluded entirely — it cannot contaminate campaign performance."
      >
        <div className="kpi-grid">
          {data.crm.map((s) => <CountKpi key={s.key} label={s.label} value={s.n} />)}
        </div>
      </Panel>

      <Panel title="What this screen cannot tell you" subtitle="Stated so the numbers above are not read as more than they are.">
        <ul style={{ margin: 0, paddingLeft: '1.2em', color: 'var(--text-muted)', fontSize: 14 }}>
          <li style={{ marginBottom: 8 }}>{data.caveats['formViewIsNotAStep']}</li>
          <li style={{ marginBottom: 8 }}>{data.caveats['verificationTraffic']}</li>
          <li>{data.caveats['noSubmissionsYet']}</li>
        </ul>
        <p style={{ fontSize: 12, color: 'var(--text-faint)', marginTop: 14, marginBottom: 0 }}>
          {NO_DATA} appears where something has not been measured, never where it was measured as zero.
        </p>
      </Panel>
    </div>
  );
}
